import React from 'react'
import { FastField, Field, useFormikContext } from 'formik'
import { Box, Grid, MenuItem, Typography } from '@mui/material'
import {
  CustomRadio,
  CustomRadioGroup,
  CustomTextField,
  FormDatePicker,
  CustomCheckboxWithoutInitialValue,
  CustomDateTimePicker,
} from 'src/form'
import { Section, SubSection } from '../common/Section'
import { IntakeNoteComprehensiveFormType } from './IntakeNoteComprehensiveFormSchema'

export function OverallInformationForm() {
  const { values } = useFormikContext<IntakeNoteComprehensiveFormType>()

  return (
    <Section title="Overall Information">
      <SubSection>
        <Grid container columnSpacing={2.5} rowSpacing={0}>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} fullWidth label="First name" name="FIRST_NAME" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} fullWidth label="Last name" name="LAST_NAME" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} fullWidth label="Kaiser MRN" name="MEMBER_ID" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={FormDatePicker} fullWidth label="Date of event" name="date" />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              component={CustomDateTimePicker}
              fullWidth
              timeOnly
              label="Call start time"
              name="CALL_START_TIME"
              referenceDateString={values.date}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Field
              component={CustomDateTimePicker}
              fullWidth
              timeOnly
              label="Call end time"
              name="CALL_END_TIME"
              referenceDateString={values.date}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} fullWidth label="Provider name" name="PROVIDER_NAME" />
          </Grid>
          <Grid item xs={12} md={4}>
            <FastField component={CustomTextField} fullWidth label="License type" name="LICENSE_TYPE" />
          </Grid>
        </Grid>
      </SubSection>

      <SubSection title="Service Delivery & Compliance">
        <Box mt={2}>
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="SERVICE_DELIVERY_AND_COMPLIANCE.CONFIRM_SERVICE_PROVIDED_VIA_TELEHEALTH"
            label="Service provided via telehealth"
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="SERVICE_DELIVERY_AND_COMPLIANCE.CONFIRM_SERVICE_PROVIDED_VIA_HIPAA_COMPLIANT_SOFTWARE"
            label="Service provided via HIPAA compliant software"
          />
          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="SERVICE_DELIVERY_AND_COMPLIANCE.CONFIRM_INTAKE_TIME_AND_LOCATION"
            label="Participant confirmed location at time of intake"
          />
        </Box>
      </SubSection>

      <SubSection title="Accessibility Needs">
        <Box mt={2}>
          <Field
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="ACCESSIBILITY_NEEDS.NON_ENGLISH_SPEAKING"
            label="Non-English speaking"
          />
          {values.ACCESSIBILITY_NEEDS.NON_ENGLISH_SPEAKING && (
            <FastField
              fullWidth
              component={CustomTextField}
              name="ACCESSIBILITY_NEEDS.LANGUAGE_USED"
              label="Language used"
              type="select"
            >
              <MenuItem value="English">English</MenuItem>
              <MenuItem value="Spanish">Spanish</MenuItem>
              <MenuItem value="French">French</MenuItem>
              <MenuItem value="German">German</MenuItem>
              <MenuItem value="Italian">Italian</MenuItem>
              <MenuItem value="Portuguese">Portuguese</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </FastField>
          )}

          <FastField
            fullWidth
            component={CustomCheckboxWithoutInitialValue}
            name="ACCESSIBILITY_NEEDS.PROVIDE_INTERPRETIVE_SERVICES"
            label="Interpretive services were provided for this interview."
          />
        </Box>

        <Box mt={2}>
          <Typography variant="h6" color="textSecondary">
            Cultural Considerations
          </Typography>

          <Field component={CustomRadioGroup} name="ACCESSIBILITY_NEEDS.CULTURAL_CONSIDERATIONS">
            <CustomRadio label="Participant reports no cultural considerations." value="reports_no" />
            <CustomRadio label="Participant reports cultural considerations." value="reports_yes" />
          </Field>
          {values.ACCESSIBILITY_NEEDS.CULTURAL_CONSIDERATIONS === 'reports_yes' && (
            <FastField
              component={CustomTextField}
              name="ACCESSIBILITY_NEEDS.CULTURAL_CONSIDERATIONS_DETAILS"
              label="Cultural Considerations"
              fullWidth
              multiline
              minRows={4}
            />
          )}
        </Box>

        <Box mt={2}>
          <Typography variant="h6" color="textSecondary">
            Access Issues
          </Typography>

          <Field component={CustomRadioGroup} name="ACCESSIBILITY_NEEDS.ACCESS_ISSUES">
            <CustomRadio label="Participant reports no access issues." value="reports_no" />
            <CustomRadio label="Participant reports access issues." value="reports_yes" />
          </Field>
          {values.ACCESSIBILITY_NEEDS.ACCESS_ISSUES === 'reports_yes' && (
            <FastField
              component={CustomTextField}
              name="ACCESSIBILITY_NEEDS.ACCESS_ISSUES_DETAILS"
              label="Access Issues"
              fullWidth
              multiline
              minRows={4}
            />
          )}
        </Box>
      </SubSection>
    </Section>
  )
}
